import { Box, VStack, HStack } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import SessionContext from '../contexts/SessionContext';
import { canMarkPartsReceived } from '../utils/deliveryReceiveAccess';
import { canOpenReporting } from '../utils/inventoryReportingAccess';
import { usePendingPurchaseOrdersCount } from '../hooks/inventory/usePendingPurchaseOrdersCount';
import { PendingCountBadge } from './PendingCountBadge';
import UserMenu from './UserMenu';
import { Button, Icon, Tooltip, type IconName } from '../ui';
import {
    invoicesListHref,
    invoicesSidebarTabFromViewState,
    loadInvoicesViewStateCache,
    readInvoicesViewStateFromLocation,
    withoutInvoicesSearch,
    type InvoicesSidebarTab,
} from '../shared/invoicesListViewState';
import { inventoryListResetState } from '../shared/inventoryListViewState';

type Tab =
    | 'todo'
    | 'activity'
    | 'dashboard'
    | 'flagged'
    | 'invoices'
    | 'invoices-flagged'
    | 'invoices-submitted'
    | 'invoices-archived'
    | 'profile'
    | 'settings'
    | 'rekey'
    | 'home'
    | 'tasks'
    | 'reporting'
    | 'chat'
    | 'ask-carl'
    | 'inventory'
    | 'inventory-flagged'
    | 'inventory-discontinued'
    | 'delivery-board';

const ALL_TABS: Array<Tab> = [
    'todo',
    'activity',
    'dashboard',
    'flagged',
    'invoices',
    'invoices-flagged',
    'invoices-submitted',
    'invoices-archived',
    'profile',
    'settings',
    'rekey',
    'home',
    'tasks',
    'reporting',
    'chat',
    'ask-carl',
    'inventory',
    'inventory-flagged',
    'inventory-discontinued',
    'delivery-board',
];

const isTab = (value: string | null): value is Tab => !!value && (ALL_TABS as Array<string>).includes(value);

/** Tabs an order page can be reached from; preserved as the active rail item. */
const ORDER_SOURCE_TABS: Array<Tab> = [
    'dashboard',
    'todo',
    'flagged',
    'invoices',
    'invoices-flagged',
    'invoices-submitted',
    'invoices-archived',
    'inventory',
    'inventory-flagged',
    'inventory-discontinued',
    'delivery-board',
    'home',
    'tasks',
];

const HOME_VIEW_STATE_CACHE_KEY = 'home_view_state_cache';
const DASHBOARD_VIEW_STATE_CACHE_KEY = 'dashboard_view_state_cache';

/**
 * Sidebar and logo restores bring back tab/page/filters but never the search:
 * a search only survives the breadcrumb trip back from a detail page.
 */
const withoutSearch = <T extends Record<string, unknown> | null | undefined>(state: T): T => (
    state ? { ...state, searchQuery: '', debouncedSearch: '' } : state
);

const restoreInvoicesList = (navigate: ReturnType<typeof useNavigate>, handleSetActiveTab: (tab: Tab) => void) => {
    const invoicesViewState = withoutInvoicesSearch(loadInvoicesViewStateCache());
    handleSetActiveTab(invoicesSidebarTabFromViewState(invoicesViewState ?? {}));
    navigate(
        invoicesListHref(invoicesViewState),
        invoicesViewState ? { state: { invoicesViewState } } : undefined,
    );
};

interface SidebarMenuProps {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    currentWidth: string;
    /** Hide the bottom collapse control (overlay / hamburger mode). */
    showCollapseToggle?: boolean;
    /** Called after a nav item is chosen (closes the overlay drawer). */
    onNavigateItem?: () => void;
}

const SidebarLabel = ({ isCollapsed, children }: { isCollapsed: boolean; children: ReactNode; }) => (
    <Box
        as="span"
        display="inline-block"
        overflow="hidden"
        whiteSpace="nowrap"
        maxW={isCollapsed ? "0" : "13.75rem"}
        opacity={isCollapsed ? 0 : 1}
        transition="max-width 80ms ease-out, opacity 60ms ease-out"
    >
        {children}
    </Box>
);

const SidebarNavContent = ({
    isCollapsed,
    icon,
    label
}: {
    isCollapsed: boolean;
    icon: ReactNode;
    label: string;
}) => {
    if (isCollapsed) {
        return <>{icon}</>;
    }
    return (
        <HStack spacing={0} w="100%" align="center">
            <Box
                w="2.25rem"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flex="0 0 auto"
            >
                {icon}
            </Box>
            <SidebarLabel isCollapsed={isCollapsed}>{label}</SidebarLabel>
        </HStack>
    );
};

interface NavEntry {
    label: string;
    icon: IconName;
    /** Path used for middle/ctrl-click "open in new tab". */
    path: string;
    isActive: boolean;
    onNavigate: () => void;
    /** Optional count badge overlayed on the button (e.g. pending POs). */
    badgeCount?: number;
}

const SidebarNavButton = ({
    entry,
    isCollapsed,
}: {
    entry: NavEntry;
    isCollapsed: boolean;
}) => {
    const { label, icon, path, isActive, onNavigate, badgeCount } = entry;
    const button = (
        <Tooltip label={label} placement="right-start" isDisabled={!isCollapsed}>
            <Button
                variant="ghost"
                bg={isActive ? 'brand.solid' : 'transparent'}
                color={isActive ? 'fg.onBrand' : 'fg.subtle'}
                _hover={{
                    bg: isActive ? 'brand.solid' : 'brand.subtle',
                    color: isActive ? 'fg.onBrand' : 'brand.fg',
                }}
                _active={{
                    bg: isActive ? 'brand.solid' : 'brand.subtle',
                    color: isActive ? 'fg.onBrand' : 'brand.fg',
                }}
                borderRadius="md"
                fontWeight="medium"
                fontSize="md"
                justifyContent={isCollapsed ? "center" : "flex-start"}
                w={isCollapsed ? "3rem" : "100%"}
                h="2.5rem"
                minW={0}
                p={isCollapsed ? 0 : undefined}
                px={isCollapsed ? 0 : 2}
                onContextMenu={(e) => {
                    e.preventDefault();
                    window.open(path, '_blank');
                }}
                onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        window.open(path, '_blank');
                        return;
                    }
                    onNavigate();
                }}
            >
                <SidebarNavContent
                    isCollapsed={isCollapsed}
                    icon={<Icon name={icon} size="lg" />}
                    label={label}
                />
            </Button>
        </Tooltip>
    );
    if (badgeCount === undefined) return button;
    return (
        <Box w={isCollapsed ? '3rem' : '100%'} position="relative" overflow="visible">
            {button}
            <PendingCountBadge count={badgeCount} />
        </Box>
    );
};

const SidebarMenu = ({
    isCollapsed,
    onToggleCollapse,
    currentWidth,
    showCollapseToggle = true,
    onNavigateItem,
}: SidebarMenuProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const sessionContext = useContext(SessionContext);
    const navSectionPx = 3;

    /** The API decides which areas a user can reach; the sidebar just mirrors that list. */
    const tabs = sessionContext?.tabs ?? [];

    const showAskCarlNav = tabs.includes('ask-carl');
    const showHomeNav = tabs.includes('ro-manager');
    // (Tasks nav gate lived here — see the parked item below; restore as
    // `tabs.includes('tasks')`.)
    const showReportingNav = canOpenReporting(sessionContext);
    const showExportsNav = tabs.includes('actvity');
    const showOrdersNav = tabs.includes('dashboard');
    /** Delivery board sits under the orders tab, and is additionally an org-level opt-in. */
    const showDeliveryBoardNav = showOrdersNav && canMarkPartsReceived(sessionContext);
    const showInventoryNav = tabs.includes('inventory');
    const showInvoicingNav = tabs.includes('invoicing');

    const { total: pendingPurchaseOrdersCount } = usePendingPurchaseOrdersCount(
        showInventoryNav,
    );

    const getInitialActiveTab = (): Tab => {
        const savedTab = localStorage.getItem('a_st');
        if (isTab(savedTab)) return savedTab;
        return 'activity';
    };

    const [activeTab, setActiveTab] = useState(getInitialActiveTab);

    useEffect(() => {
        const path = location.pathname;
        const determineTab = (): Tab | null => {
            if (path.startsWith('/todo')) return 'todo';
            // Check if we're on an order page and preserve the source tab if available
            if (path.startsWith('/order/')) {
                const source = location.state?.source as Tab | undefined;
                if (source && ORDER_SOURCE_TABS.includes(source)) {
                    return source;
                }
                // If no source provided, preserve current activeTab if it's a valid tab for order pages
                if (ORDER_SOURCE_TABS.includes(activeTab)) {
                    return activeTab;
                }
                return 'activity'; // Default fallback
            }
            if (path === '/home' || path === '/') return 'home';
            if (path.startsWith('/tasks')) return 'tasks';
            if (path.startsWith('/chat')) return 'chat';
            if (path.startsWith('/reporting')) return 'reporting';
            if (path.startsWith('/ask-carl')) return 'ask-carl';
            if (path.startsWith('/activity')) return 'activity';
            if (path.startsWith('/dashboard')) return 'dashboard';
            if (path.startsWith('/delivery-board')) return 'delivery-board';
            if (path.startsWith('/rekey')) return 'rekey';
            if (path.startsWith('/flagged')) return 'flagged';
            if (path.startsWith('/invoices')) {
                const segments = path.split('/').filter(Boolean);
                const isInvoiceDetail = segments.length >= 2 && segments[0] === 'invoices';
                if (isInvoiceDetail) {
                    const source = location.state?.source as Tab | undefined;
                    if (
                        source === 'invoices' ||
                        source === 'invoices-flagged' ||
                        source === 'invoices-submitted' ||
                        source === 'invoices-archived'
                    ) {
                        return source;
                    }
                    const fromView = invoicesSidebarTabFromViewState(
                        readInvoicesViewStateFromLocation(location.state) ?? loadInvoicesViewStateCache() ?? {},
                    ) as InvoicesSidebarTab;
                    if (
                        activeTab === 'invoices' ||
                        activeTab === 'invoices-flagged' ||
                        activeTab === 'invoices-submitted' ||
                        activeTab === 'invoices-archived'
                    ) {
                        return activeTab;
                    }
                    return fromView;
                }
                const q = new URLSearchParams(location.search);
                if (q.get('archived') === 'true') return 'invoices-archived';
                if (q.get('type') === 'flagged') return 'invoices-flagged';
                if (q.get('type') === 'submitted') return 'invoices-submitted';
                return 'invoices';
            }
            if (path.startsWith('/inventory')) {
                const q = new URLSearchParams(location.search);
                if (q.get('statusFilter') === 'flagged') return 'inventory-flagged';
                if (q.get('statusFilter') === 'discontinued') return 'inventory-discontinued';
                return 'inventory';
            }
            // Profile lives inside the Settings takeover (/settings/profile).
            if (path.startsWith('/settings/profile')) return 'profile';
            if (path.startsWith('/settings')) return 'settings';
            return null;
        };
        const nextTab = determineTab();
        if (nextTab && nextTab !== activeTab) {
            setActiveTab(nextTab);
            localStorage.setItem('a_st', nextTab);
        }
    }, [location.pathname, location.search, location.state, activeTab]);

    const handleSetActiveTab = (tab: Tab) => {
        setActiveTab(tab);
        localStorage.setItem('a_st', tab);
    };

    const getCachedViewState = (cacheKey: string): Record<string, unknown> | undefined => {
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (!cached) return undefined;
            return JSON.parse(cached) as Record<string, unknown>;
        } catch {
            return undefined;
        }
    };

    const isActive = (tab: Tab) => activeTab === tab;
    const isInvoicingNavActive =
        isActive('invoices') ||
        isActive('invoices-flagged') ||
        isActive('invoices-submitted') ||
        isActive('invoices-archived');
    const isInventoryNavActive =
        isActive('inventory') ||
        isActive('inventory-flagged') ||
        isActive('inventory-discontinued');

    const goInventoryList = (tab: Tab, search: string) => {
        try {
            localStorage.setItem('inventory_list_page', '1');
        } catch {
            // ignore
        }
        handleSetActiveTab(tab);
        // Inventory keeps its search in component state; the flag tells an
        // already-mounted list to drop it (see src/shared/inventoryListViewState).
        navigate({ pathname: '/inventory', search }, { state: inventoryListResetState() });
    };

    /**
     * Flat, ordered rail (ENG-2164): Ask Carl, Home, Tasks, Reporting, Exports,
     * Orders, Inventory, Invoicing — no section headers. Users missing a tab
     * simply skip that button. Delivery Board rides along under Orders as an
     * org-level opt-in.
     */
    const navEntries: Array<NavEntry> = [
        ...(showAskCarlNav ? [{
            label: 'Ask Carl',
            icon: 'sparkles' as const,
            path: '/ask-carl',
            isActive: isActive('ask-carl'),
            onNavigate: () => {
                handleSetActiveTab('ask-carl');
                navigate('/ask-carl');
            },
        }] : []),
        ...(showHomeNav ? [{
            label: 'Home',
            icon: 'home' as const,
            path: '/home',
            isActive: isActive('home'),
            onNavigate: () => {
                handleSetActiveTab('home');
                const homeViewState = withoutSearch(getCachedViewState(HOME_VIEW_STATE_CACHE_KEY));
                navigate('/home', homeViewState ? { state: { homeViewState } } : undefined);
            },
        }] : []),
        // Tasks nav is parked until the /tasks route ships — App.tsx has no
        // such route, so the (tab-gated) item was a guaranteed 404. The
        // `tasks` WebsiteTab key and permission label stay so nothing
        // persisted changes when it comes back.
        // ...(showTasksNav ? [{ label: 'Tasks', path: '/tasks', ... }] : []),
        ...(showReportingNav ? [{
            label: 'Reporting',
            icon: 'chartBar' as const,
            path: '/reporting',
            isActive: isActive('reporting'),
            onNavigate: () => {
                handleSetActiveTab('reporting');
                navigate('/reporting');
            },
        }] : []),
        ...(showExportsNav ? [{
            label: 'Exports',
            icon: 'fileExport' as const,
            path: '/activity',
            isActive: isActive('activity'),
            onNavigate: () => {
                handleSetActiveTab('activity');
                navigate('/activity');
            },
        }] : []),
        ...(showOrdersNav ? [{
            label: 'Orders',
            icon: 'package' as const,
            path: '/dashboard',
            isActive: isActive('dashboard'),
            onNavigate: () => {
                handleSetActiveTab('dashboard');
                const dashboardViewState = withoutSearch(getCachedViewState(DASHBOARD_VIEW_STATE_CACHE_KEY));
                navigate('/dashboard', dashboardViewState ? { state: { dashboardViewState } } : undefined);
            },
        }] : []),
        ...(showDeliveryBoardNav ? [{
            label: 'Delivery Board',
            icon: 'truck' as const,
            path: '/delivery-board',
            isActive: isActive('delivery-board'),
            onNavigate: () => {
                handleSetActiveTab('delivery-board');
                navigate('/delivery-board');
            },
        }] : []),
        ...(showInventoryNav ? [{
            label: 'Inventory',
            icon: 'warehouse' as const,
            path: '/inventory',
            isActive: isInventoryNavActive,
            onNavigate: () => goInventoryList('inventory', ''),
            badgeCount: pendingPurchaseOrdersCount,
        }] : []),
        ...(showInvoicingNav ? [{
            label: 'Invoicing',
            icon: 'fileText' as const,
            path: '/invoices',
            isActive: isInvoicingNavActive,
            onNavigate: () => restoreInvoicesList(navigate, handleSetActiveTab),
        }] : []),
    ];

    const handleChromeClick = (event: MouseEvent<HTMLElement>) => {
        if (!showCollapseToggle) return;
        const target = event.target as HTMLElement | null;
        if (target?.closest('button, a, [role="button"], input, textarea, select, [role="menuitem"]')) {
            return;
        }
        onToggleCollapse();
    };

    return (
        <Box
            as="nav"
            bg="gray.100"
            w={currentWidth}
            h="100%"
            pt={3}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            cursor={showCollapseToggle ? 'pointer' : undefined}
            onClick={handleChromeClick}
        >
            {/* Head — account block (ENG-2297: rail owns the top-left corner). */}
            <Box px={isCollapsed ? 3 : navSectionPx} pb={3}>
                <UserMenu isCollapsed={isCollapsed} />
            </Box>

            <VStack alignItems="flex-start" w="100%" px={isCollapsed ? 3 : navSectionPx} gap={2}>
                {navEntries.map((entry) => (
                    <SidebarNavButton
                        key={entry.label}
                        entry={{
                            ...entry,
                            onNavigate: () => {
                                entry.onNavigate();
                                onNavigateItem?.();
                            },
                        }}
                        isCollapsed={isCollapsed}
                    />
                ))}
            </VStack>

            {/* Bottom-left corner — Customer Support (opens the Pylon messenger;
                the floating bubble is retired). No collapse button: clicking the
                rail chrome still toggles collapse. */}
            <Box mt="auto" mb={4} px={isCollapsed ? 3 : navSectionPx}>
                <Tooltip label="Customer Support" placement="right">
                    <Button
                        variant="ghost"
                        borderRadius="md"
                        w={isCollapsed ? "3rem" : "100%"}
                        h="2.5rem"
                        minW={0}
                        bg="transparent"
                        color="fg.subtle"
                        fontWeight="medium"
                        fontSize="md"
                        justifyContent={isCollapsed ? "center" : "flex-start"}
                        p={isCollapsed ? 0 : undefined}
                        px={isCollapsed ? 0 : 2}
                        _hover={{
                            bg: 'brand.subtle',
                            color: 'brand.fg',
                        }}
                        onClick={() => {
                            window.Pylon?.('show');
                            onNavigateItem?.();
                        }}
                    >
                        <SidebarNavContent
                            isCollapsed={isCollapsed}
                            icon={<Icon name="headset" size="lg" />}
                            label="Support"
                        />
                    </Button>
                </Tooltip>
            </Box>
        </Box >
    );
};

export default SidebarMenu;
